import { Result } from "@bitrix24/b24jssdk";
import type {
  B24Frame,
  AjaxResult,
  BatchNamedCommandsUniversal,
  CallBatchResult,
} from "@bitrix24/b24jssdk";
import { ResponseCache } from "./ResponseCache";
import type { AbortToken } from "../hooks/useAbortToken";
import { AbortError } from "../hooks/useAbortToken";

// ── Constants ─────────────────────────────────────────────────────────────────

/** Bitrix24 hard limit: max commands per single batch HTTP request. */
const BATCH_LIMIT = 50;

/**
 * Retry configuration for transient failures (network errors, timeouts).
 * SDK already handles QUERY_LIMIT_EXCEEDED internally — these retries cover
 * transport-level failures only.
 */
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1_000,
  maxDelayMs: 32_000,
  jitterMs: 500,
} as const;

/** Default debounce window for enqueue() queue flushing (ms). */
const DEFAULT_DEBOUNCE_MS = 50;

// ── Internal types ────────────────────────────────────────────────────────────

interface QueuedCommand {
  method: string;
  params: object;
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
}

// ── BatchOrchestrator ─────────────────────────────────────────────────────────

/**
 * BatchOrchestrator — the single gateway for all Bitrix24 REST API calls.
 *
 * All CRM data-fetching hooks and components MUST route requests through this
 * class instead of calling `$b24.callMethod()` or `$b24.actions.v2.*` directly.
 * (Data Storage CRUD in StorageService/SectionService is the only exemption.)
 *
 * Three modes of operation:
 *
 *  1. **call(method, params)**
 *     Single REST call via `actions.v2.call.make()`. Returns `AjaxResult<T>`
 *     with standard `.isSuccess`, `.getData()`, `.getErrorMessages()`.
 *
 *  2. **executeBatch(calls, options?)**
 *     Named-command batch via `actions.v2.batch.make()`. Auto-chunks payloads
 *     exceeding the 50-command limit into sequential HTTP round-trips and
 *     merges results transparently.
 *
 *  3. **enqueue(key, method, params)**
 *     Debounced aggregation queue. Multiple concurrent component calls are
 *     collected during the debounce window, then flushed as a single batch.
 *     Ideal for non-paginated lookups (e.g. `crm.category.list`).
 *
 * All modes wrap network calls in exponential-backoff retry logic for
 * transient failures. The SDK's built-in rate limiter handles
 * `QUERY_LIMIT_EXCEEDED` responses automatically.
 */
export class BatchOrchestrator {
  private readonly $b24: B24Frame;
  private readonly debounceMs: number;
  private readonly cache: ResponseCache;
  private queue: Map<string, QueuedCommand> = new Map();
  private timer: ReturnType<typeof setTimeout> | null = null;

  /**
   * All currently in-flight abort tokens registered by call() / executeBatch().
   * Enables a single cancelAll() to abort every active request sequence.
   * Tokens are auto-removed when they fire (via onCancel callback).
   */
  private readonly activeTokens = new Set<AbortToken>();

  constructor(b24: B24Frame, debounceMs = DEFAULT_DEBOUNCE_MS) {
    this.$b24 = b24;
    this.debounceMs = debounceMs;
    this.cache = new ResponseCache();
  }

  /** Prune expired cache entries. Call on app mount or periodically. */
  pruneCache(): void {
    this.cache.prune();
  }

  /** Force-clear the entire response cache. */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Registers an AbortToken so it can be cancelled by `cancelAll()`.
   * The token is automatically removed from the set when it fires.
   */
  registerToken(token: AbortToken): void {
    this.activeTokens.add(token);
    token.onCancel(() => {
      this.activeTokens.delete(token);
    });
  }

  /**
   * Cancels every active AbortToken registered via `registerToken()`.
   *
   * Note: This cannot abort HTTP requests already on the wire — the Bitrix24
   * SDK does not expose an AbortController. Cancellation prevents processing
   * of in-flight results and stops multi-chunk sequences from continuing.
   */
  cancelAll(reason?: string): void {
    for (const token of this.activeTokens) {
      token.cancel(reason ?? "cancelAll");
    }
    this.activeTokens.clear();
  }

  /** Returns the count of currently tracked in-flight tokens. */
  get activeRequestCount(): number {
    return this.activeTokens.size;
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Executes a single Bitrix24 REST method via `actions.v2.call.make()`.
   *
   * Returns `AjaxResult<T>` — callers inspect `.isSuccess` and call `.getData()`.
   *
   * Wrapped in retry logic for transient network failures.
   *
   * @example
   * const res = await orchestrator.call('crm.item.list', {
   *   entityTypeId: 128,
   *   select: ['id', 'title'],
   *   start: 0,
   * })
   * const items = (res.getData() as { result?: { items?: CrmItem[] } })?.result?.items ?? []
   */
  async call(
    method: string,
    params: object = {},
    options?: { skipCache?: boolean; abortToken?: AbortToken },
  ): Promise<AjaxResult> {
    const skipCache = options?.skipCache ?? false;
    const token = options?.abortToken;
    if (token) this.registerToken(token);

    if (token?.cancelled) throw new AbortError(token.reason);

    if (!skipCache) {
      const cacheKey = ResponseCache.buildKey(method, params);
      const cached = this.cache.get(cacheKey);
      if (cached !== undefined) {
        // The cached value is the SuccessPayload from getData().
        // Build a shim implementing the AjaxResult interface.
        const payload = cached as Record<string, unknown>;
        return {
          isSuccess: true,
          getData: () => cached,
          getErrorMessages: () => [],
          getErrors: () => [],
          getTotal: () => (typeof payload["total"] === "number" ? payload["total"] : 0),
          isMore: () => payload["next"] !== undefined,
          hasMore: () => payload["next"] !== undefined,
          getStatus: () => 200,
        } as unknown as AjaxResult;
      }
    }

    const result = await this.withRetry(() =>
      this.$b24.actions.v2.call.make({
        method,
        params: params,
      }),
    );

    if (token?.cancelled) throw new AbortError(token.reason);

    if (!skipCache && result.isSuccess) {
      const cacheKey = ResponseCache.buildKey(method, params);
      this.cache.set(cacheKey, result.getData(), method);
    }

    return result;
  }

  /**
   * Executes a named-command batch via `actions.v2.batch.make()`.
   *
   * Automatically chunks payloads exceeding 50 commands into sequential
   * HTTP round-trips and merges results into a single `CallBatchResult`.
   *
   * @param calls  Named commands — `{ key: { method, params } }`.
   * @param options.isHaltOnError  Stop on first command error (default: false).
   *
   * @example
   * const result = await orchestrator.executeBatch({
   *   contacts: { method: 'crm.item.list', params: { entityTypeId: 3, ... } },
   *   companies: { method: 'crm.item.list', params: { entityTypeId: 4, ... } },
   * })
   * const data = result.getData() as Record<string, unknown>
   */
  async executeBatch(
    calls: BatchNamedCommandsUniversal,
    options: {
      isHaltOnError?: boolean;
      skipCache?: boolean;
      abortToken?: AbortToken;
      returnAjaxResult?: boolean;
    } = {},
  ): Promise<CallBatchResult<unknown>> {
    const callsObj = calls as Record<string, { method: string; params: Record<string, unknown> }>;
    const keys = Object.keys(callsObj);
    const haltOnError = options.isHaltOnError ?? false;
    const returnAjax = options.returnAjaxResult ?? false;
    const token = options.abortToken;
    if (token) this.registerToken(token);

    if (token?.cancelled) throw new AbortError(token.reason);

    // Batch results are NOT shared with the ResponseCache used by call().
    // The two paths return values in incompatible formats:
    //   call()        → { result: { items: [...] }, total: N }  (single-call SDK wrapper)
    //   executeBatch  → { items: [...], total: N }              (bare batch entry, no wrapper)
    // Cross-caching the two formats causes callers of call() that read
    // raw?.result?.items to receive undefined when the cached entry was written
    // by executeBatch, producing an empty column. Always execute the batch fresh.

    // Fast path: fits in one batch — no chunking overhead
    if (keys.length <= BATCH_LIMIT) {
      const result = await this.withRetry(() =>
        this.$b24.actions.v2.batch.make({
          calls,
          options: { isHaltOnError: haltOnError, returnAjaxResult: returnAjax },
        }),
      );
      return result;
    }

    // Chunked path: split named commands into ≤50 groups, execute sequentially
    const merged: Record<string, unknown> = {};

    for (let i = 0; i < keys.length; i += BATCH_LIMIT) {
      if (token?.cancelled) throw new AbortError(token.reason);

      const chunkKeys = keys.slice(i, i + BATCH_LIMIT);
      const chunkCalls = Object.fromEntries(
        chunkKeys.map((k) => [k, callsObj[k]]),
      ) as BatchNamedCommandsUniversal;

      const result = await this.withRetry(() =>
        this.$b24.actions.v2.batch.make({
          calls: chunkCalls,
          options: { isHaltOnError: haltOnError, returnAjaxResult: returnAjax },
        }),
      );

      if (token?.cancelled) throw new AbortError(token.reason);

      if (!result.isSuccess) return result;

      Object.assign(merged, result.getData() as Record<string, unknown>);
    }

    return Result.ok(merged);
  }

  /**
   * Queues a non-paginated command for the next debounced batch flush.
   *
   * Multiple callers' commands are aggregated during the debounce window
   * (default 50 ms), then flushed as a single `batch.make()` call.
   * If the same key is enqueued again before flush, the newer params win
   * (deduplication).
   *
   * @param key     Unique command identifier — used as the batch command key
   *                and for deduplication. Same key overwrites previous entry.
   * @param method  Bitrix24 REST method name (e.g. `crm.category.list`).
   * @param params  Method parameters.
   * @returns       Resolves with the individual command result from the batch.
   */
  enqueue(key: string, method: string, params: object): Promise<unknown> {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    const promise = new Promise<unknown>((resolve, reject) => {
      this.queue.set(key, { method, params, resolve, reject });
    });

    this.timer = setTimeout(() => {
      void this.flush();
    }, this.debounceMs);
    return promise;
  }

  // ── Private internals ─────────────────────────────────────────────────────

  /**
   * Retries `fn` with exponential backoff on transient failures.
   *
   * Only retries on thrown exceptions (network errors, timeouts) — NOT on
   * `isSuccess === false` results, which indicate business-logic errors
   * returned by the Bitrix24 API.
   *
   * The SDK's built-in rate limiter handles `QUERY_LIMIT_EXCEEDED` responses
   * internally, so this retry layer does not duplicate that concern.
   */
  private async withRetry<T>(fn: () => Promise<T>): Promise<T> {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
      try {
        return await fn();
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        if (attempt === RETRY_CONFIG.maxRetries) break;

        const delay =
          Math.min(RETRY_CONFIG.baseDelayMs * 2 ** attempt, RETRY_CONFIG.maxDelayMs) +
          Math.random() * RETRY_CONFIG.jitterMs;

        await new Promise<void>((resolve) => setTimeout(resolve, delay));
      }
    }

    throw lastError!;
  }

  /**
   * Flushes the debounce queue as chunked batch calls.
   *
   * Drains the queue atomically, splits into ≤50-command chunks, and
   * resolves/rejects each caller's promise individually.
   */
  private async flush(): Promise<void> {
    this.timer = null;
    if (this.queue.size === 0) return;

    const batch = new Map(this.queue);
    this.queue.clear();

    const entries = [...batch.entries()];

    for (let i = 0; i < entries.length; i += BATCH_LIMIT) {
      const chunk = entries.slice(i, i + BATCH_LIMIT);
      const commands: Record<string, { method: string; params: Record<string, unknown> }> = {};

      for (const [key, cmd] of chunk) {
        commands[key] = {
          method: cmd.method,
          params: cmd.params as Record<string, unknown>,
        };
      }

      try {
        const result = await this.withRetry(() =>
          this.$b24.actions.v2.batch.make({
            calls: commands,
            options: { isHaltOnError: false },
          }),
        );

        if (!result.isSuccess) {
          throw new Error(result.getErrorMessages().join(", "));
        }
        const data = result.getData() as Record<string, unknown>;
        for (const [key, cmd] of chunk) {
          cmd.resolve(data[key]);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        for (const [, cmd] of chunk) {
          cmd.reject(error);
        }
      }
    }
  }
}
